/**
 * Local library cover display precedence — local art always wins over enriched
 * cover_art_url once resolution completes.
 */

import { isBadCoverUrl } from './coverArt';

export type LocalCoverPrecedenceInput = {
  localCoverUrl?: string | null;
  /** LocalAlbumPage IPC effect → album.cover / album.artworkUrl (shared object state). */
  albumObjectCoverUrl?: string | null;
  onlineCoverUrl?: string | null;
  overrideSource?: string | null;
  /** Embedded/folder hash or on-disk folder path — local IPC may still be pending */
  hasLocalArtSource?: boolean;
  /** Hash/path IPC finished without a local URL */
  localResolutionComplete?: boolean;
  isRemoteTunnel?: boolean;
  /**
   * Grid tiles: when local art was expected but IPC found nothing, show placeholder
   * instead of enriched cover_art_url. Detail page leaves this false.
   */
  gridPreferPlaceholderOverEnriched?: boolean;
};

/** Valid raster cover on the album object — never SVG title placeholders. */
export function pickValidAlbumObjectCoverUrl(
  ...candidates: (string | null | undefined)[]
): string | null {
  for (const raw of candidates) {
    if (!raw || isBadCoverUrl(raw)) continue;
    const s = String(raw).trim();
    if (
      /^data:image\/(jpeg|png|webp);/i.test(s)
      || /^blob:/i.test(s)
      || /^https?:\/\//i.test(s)
    ) {
      return s;
    }
  }
  return null;
}

/** True when the album index row carries embedded or folder cover hashes. */
export function hasEmbeddedOrFolderCoverHash(
  embeddedCoverHash?: string | null,
  folderCoverHash?: string | null,
): boolean {
  const emb = String(embeddedCoverHash || '').trim();
  const fol = String(folderCoverHash || '').trim();
  return emb.length > 0 || fol.length > 0;
}

/**
 * Enriched cover_art_url (CAA etc.) is allowed only for genuinely artless albums —
 * no embedded/folder hash on the index row, artwork_source enrichment or none.
 */
export function mayUseEnrichedCoverUrl(args: {
  artworkSource?: string | null;
  embeddedCoverHash?: string | null;
  folderCoverHash?: string | null;
}): boolean {
  if (hasEmbeddedOrFolderCoverHash(args.embeddedCoverHash, args.folderCoverHash)) {
    return false;
  }
  const src = String(args.artworkSource || '').trim().toLowerCase();
  return src === 'enrichment' || src === 'none' || src === '';
}

/** Pick the cover URL to display for a local-library album tile or detail header. */
export function pickLocalLibraryDisplayCover(input: LocalCoverPrecedenceInput): string | null {
  const {
    localCoverUrl,
    albumObjectCoverUrl,
    onlineCoverUrl,
    overrideSource,
    hasLocalArtSource = false,
    localResolutionComplete = false,
    isRemoteTunnel = false,
    gridPreferPlaceholderOverEnriched = false,
  } = input;

  const local = localCoverUrl && !isBadCoverUrl(localCoverUrl) ? localCoverUrl : null;
  const sharedAlbum = pickValidAlbumObjectCoverUrl(albumObjectCoverUrl) || null;
  const online = onlineCoverUrl || null;

  if (overrideSource === 'embedded' || overrideSource === 'folder') {
    return local || sharedAlbum || online;
  }
  if (overrideSource === 'online') return online || local || sharedAlbum;
  if (overrideSource === 'none') return null;
  if (isRemoteTunnel) return local || sharedAlbum || online;

  if (local) return local;
  if (hasLocalArtSource && !localResolutionComplete) return null;
  if (gridPreferPlaceholderOverEnriched && hasLocalArtSource && localResolutionComplete) {
    return null;
  }
  if (localResolutionComplete && sharedAlbum) return sharedAlbum;
  return online && !isBadCoverUrl(online) ? online : null;
}

/** True when index row has no hashes but detail IPC or folder path may still yield local art. */
export function shouldDeferEnrichedCoverForLocalAlbum(args: {
  isLocalLibraryAlbum: boolean;
  indexHashesEmpty: boolean;
  albumFolderPath?: string | null;
  detailHashPending?: boolean;
}): boolean {
  if (!args.isLocalLibraryAlbum) return false;
  if (!args.indexHashesEmpty) return false;
  return !!(args.albumFolderPath || args.detailHashPending);
}
